import { useEffect, useMemo, useRef } from 'react'
import Globe from 'globe.gl'
import createGlobe, {
  type COBEOptions,
  type Globe as CobeGlobe,
  type Marker,
} from 'cobe'
import { ShaderMaterial, TextureLoader, Vector2 } from 'three'
import type { ThemeMode } from './themeHandler'
import './LeaderboardQuestionResponseGlobe.css'

export type LeaderboardQuestionResponseLocationPoint = {
  lat: number
  lng: number
  count: number
  countryCode: string
}

export type LeaderboardQuestionResponseGlobeProps = {
  id: string
  points: LeaderboardQuestionResponseLocationPoint[]
  responseEventsUrl?: string | null
  responseCount: number
  theme: ThemeMode
}

type GlobeInstance = InstanceType<typeof Globe>
type GlobeRenderer = 'cobe' | 'globe-gl'
type LeaderboardQuestionResponseEventPoint = {
  id: number
  lat: number
  lng: number
  countryCode: string
  responseTime: string
}
type ResponseEventPage = {
  items: LeaderboardQuestionResponseEventPoint[]
  nextAfterId: number | null
  nextAfterResponseTime: string | null
  hasMore: boolean
}
type CobePingTarget = {
  lat: number
  lng: number
  maxRadius: number
  createdAt: number
  id: string
}

const numberFormatter = new Intl.NumberFormat()
const millisecondsPerDay = 86_400_000
const responseEventPageSize = 200
const playbackDurationMs = 5 * 60 * 1000
const playbackTickMs = 200
const targetLifetimeMs = 2800
const maxLiveCobeTargets = 1400
const initialCobePhi = -1.5
const initialCobeTheta = 0.38

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

const getSelectedRenderer = (): GlobeRenderer => {
  const value = new URLSearchParams(window.location.search)
    .get('globe')
    ?.toLowerCase()

  if (value === 'cobe') return 'cobe'
  if (value === 'globegl' || value === 'globe-gl') return 'globe-gl'

  return 'cobe'
}

const dayNightShader = {
  vertexShader: `
    varying vec3 vNormal;
    varying vec2 vUv;

    void main() {
      vNormal = normalize(normalMatrix * normal);
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    #define PI 3.141592653589793

    uniform sampler2D dayTexture;
    uniform sampler2D nightTexture;
    uniform vec2 sunPosition;
    uniform vec2 globeRotation;

    varying vec3 vNormal;
    varying vec2 vUv;

    float toRad(in float angle) {
      return angle * PI / 180.0;
    }

    vec3 polar2Cartesian(in vec2 coordinate) {
      float theta = toRad(90.0 - coordinate.x);
      float phi = toRad(90.0 - coordinate.y);

      return vec3(
        sin(phi) * cos(theta),
        cos(phi),
        sin(phi) * sin(theta)
      );
    }

    void main() {
      float invLon = toRad(globeRotation.x);
      float invLat = -toRad(globeRotation.y);

      mat3 rotX = mat3(
        1, 0, 0,
        0, cos(invLat), -sin(invLat),
        0, sin(invLat), cos(invLat)
      );

      mat3 rotY = mat3(
        cos(invLon), 0, sin(invLon),
        0, 1, 0,
        -sin(invLon), 0, cos(invLon)
      );

      vec3 rotatedSunDirection = rotX * rotY * polar2Cartesian(sunPosition);
      float intensity = dot(normalize(vNormal), normalize(rotatedSunDirection));
      vec4 dayColor = texture2D(dayTexture, vUv);
      vec4 nightColor = texture2D(nightTexture, vUv);
      float blendFactor = smoothstep(-0.1, 0.1, intensity);

      gl_FragColor = mix(nightColor, dayColor, blendFactor);
    }
  `,
}

const toJulianCentury = (date: Date) =>
  (date.getTime() / millisecondsPerDay + 2440587.5 - 2451545) / 36525

const normalizeDegrees = (degrees: number) =>
  ((degrees % 360) + 360) % 360

const solarDeclination = (julianCentury: number) => {
  const meanLongitude = normalizeDegrees(
    280.46646 + julianCentury * (36000.76983 + julianCentury * 0.0003032),
  )
  const meanAnomaly = normalizeDegrees(
    357.52911 + julianCentury * (35999.05029 - 0.0001537 * julianCentury),
  )
  const anomalyRadians = (meanAnomaly * Math.PI) / 180
  const equationOfCenter =
    Math.sin(anomalyRadians) *
      (1.914602 - julianCentury * (0.004817 + 0.000014 * julianCentury)) +
    Math.sin(2 * anomalyRadians) * (0.019993 - 0.000101 * julianCentury) +
    Math.sin(3 * anomalyRadians) * 0.000289
  const trueLongitude = meanLongitude + equationOfCenter
  const omega = 125.04 - 1934.136 * julianCentury
  const apparentLongitude =
    trueLongitude - 0.00569 - 0.00478 * Math.sin((omega * Math.PI) / 180)
  const meanObliquitySeconds =
    21.448 -
    julianCentury *
      (46.815 + julianCentury * (0.00059 - julianCentury * 0.001813))
  const meanObliquity = 23 + (26 + meanObliquitySeconds / 60) / 60
  const obliquity =
    meanObliquity + 0.00256 * Math.cos((omega * Math.PI) / 180)
  const declinationRadians = Math.asin(
    Math.sin((obliquity * Math.PI) / 180) *
      Math.sin((apparentLongitude * Math.PI) / 180),
  )

  return (declinationRadians * 180) / Math.PI
}

const equationOfTime = (julianCentury: number) => {
  const obliquity = 23.439291 - 0.0130042 * julianCentury
  const meanLongitude = normalizeDegrees(
    280.46646 + julianCentury * (36000.76983 + julianCentury * 0.0003032),
  )
  const eccentricity =
    0.016708634 -
    julianCentury * (0.000042037 + 0.0000001267 * julianCentury)
  const meanAnomaly = normalizeDegrees(
    357.52911 + julianCentury * (35999.05029 - 0.0001537 * julianCentury),
  )
  const y = Math.tan((obliquity * Math.PI) / 360) ** 2
  const meanLongitudeRadians = (meanLongitude * Math.PI) / 180
  const meanAnomalyRadians = (meanAnomaly * Math.PI) / 180
  const minutes =
    y * Math.sin(2 * meanLongitudeRadians) -
    2 * eccentricity * Math.sin(meanAnomalyRadians) +
    4 *
      eccentricity *
      y *
      Math.sin(meanAnomalyRadians) *
      Math.cos(2 * meanLongitudeRadians) -
    0.5 * y * y * Math.sin(4 * meanLongitudeRadians) -
    1.25 * eccentricity * eccentricity * Math.sin(2 * meanAnomalyRadians)

  return (minutes * 180 * 4) / Math.PI
}

const sunPositionAt = (date: Date): [number, number] => {
  const startOfDay = new Date(date)
  startOfDay.setUTCHours(0, 0, 0, 0)

  const julianCentury = toJulianCentury(date)
  const longitude =
    ((startOfDay.getTime() - date.getTime()) / millisecondsPerDay) * 360 - 180

  return [
    longitude - equationOfTime(julianCentury) / 4,
    solarDeclination(julianCentury),
  ]
}

const formatCoordinate = (value: number, positive: string, negative: string) => {
  const suffix = value >= 0 ? positive : negative
  return `${Math.abs(value).toFixed(2)}${suffix}`
}

const labelForPoint = (point: LeaderboardQuestionResponseLocationPoint) =>
  `${point.countryCode || 'Unknown'} · ${numberFormatter.format(point.count)} responses · ${formatCoordinate(point.lat, 'N', 'S')}, ${formatCoordinate(point.lng, 'E', 'W')}`

const altitudeForPoint = (
  point: LeaderboardQuestionResponseLocationPoint,
  maxCount: number,
) => 0.012 + Math.sqrt(point.count / maxCount) * 0.42

const radiusForPoint = (
  point: LeaderboardQuestionResponseLocationPoint,
  maxCount: number,
) => 0.16 + Math.sqrt(point.count / maxCount) * 0.42

const colorForPoint = (
  point: LeaderboardQuestionResponseLocationPoint,
  maxCount: number,
) => {
  const intensity = point.count / maxCount
  if (intensity > 0.66) return '#ffb85c'
  if (intensity > 0.28) return '#35d0ba'
  return '#8eb7ff'
}

const cobeMarkersForTargets = (targets: CobePingTarget[], now: number): Marker[] =>
  targets.flatMap((target) => {
    const progress = Math.min(1, Math.max(0, (now - target.createdAt) / targetLifetimeMs))
    const scale = target.maxRadius / 3.6
    const location: [number, number] = [target.lat, target.lng]

    return [
      {
        location,
        size: (0.018 + progress * 0.065) * scale,
        color: [1, 1, 1],
      },
      {
        location,
        size: (0.015 + (1 - progress) * 0.025) * scale,
        color: [1, 0.36, 0.84],
      },
    ]
  })

function GlobeGlLeaderboardQuestionResponseGlobe({
  points,
}: LeaderboardQuestionResponseGlobeProps) {
  const globeRootRef = useRef<HTMLDivElement | null>(null)

  const maxCount = useMemo(
    () => Math.max(1, ...points.map((point) => point.count)),
    [points],
  )

  useEffect(() => {
    const root = globeRootRef.current
    if (!root) return undefined

    let disposed = false
    let sunInterval = 0
    let dayNightMaterial: ShaderMaterial | null = null
    let globe: GlobeInstance | null = new Globe(root)
      .backgroundColor('rgba(0,0,0,0)')
      .globeImageUrl('//unpkg.com/three-globe/example/img/earth-night.jpg')
      .bumpImageUrl('//unpkg.com/three-globe/example/img/earth-topology.png')
      .showAtmosphere(true)
      .atmosphereColor('#35d0ba')
      .atmosphereAltitude(0.16)
      .pointsData(points)
      .pointLat('lat')
      .pointLng('lng')
      .pointAltitude((point: object) =>
        altitudeForPoint(point as LeaderboardQuestionResponseLocationPoint, maxCount),
      )
      .pointRadius((point: object) =>
        radiusForPoint(point as LeaderboardQuestionResponseLocationPoint, maxCount),
      )
      .pointColor((point: object) =>
        colorForPoint(point as LeaderboardQuestionResponseLocationPoint, maxCount),
      )
      .pointLabel((point: object) =>
        labelForPoint(point as LeaderboardQuestionResponseLocationPoint),
      )
      .pointsMerge(true)
      .pointResolution(12)
      .pointsTransitionDuration(900)

    const controls = globe.controls()
    controls.autoRotate = true
    controls.autoRotateSpeed = 0.35
    controls.enableDamping = true
    globe.pointOfView({ lat: 26, lng: -22, altitude: 2.05 }, 0)

    const setSunPosition = () => {
      if (!dayNightMaterial) return
      dayNightMaterial.uniforms.sunPosition.value.set(...sunPositionAt(new Date()))
    }

    const textureLoader = new TextureLoader()
    Promise.all([
      textureLoader.loadAsync('//cdn.jsdelivr.net/npm/three-globe/example/img/earth-day.jpg'),
      textureLoader.loadAsync('//cdn.jsdelivr.net/npm/three-globe/example/img/earth-night.jpg'),
    ]).then(([dayTexture, nightTexture]) => {
      if (disposed || !globe) {
        dayTexture.dispose()
        nightTexture.dispose()
        return
      }

      dayNightMaterial = new ShaderMaterial({
        uniforms: {
          dayTexture: { value: dayTexture },
          nightTexture: { value: nightTexture },
          sunPosition: { value: new Vector2() },
          globeRotation: { value: new Vector2() },
        },
        vertexShader: dayNightShader.vertexShader,
        fragmentShader: dayNightShader.fragmentShader,
      })

      globe.globeMaterial(dayNightMaterial).onZoom(({ lng, lat }) => {
        dayNightMaterial?.uniforms.globeRotation.value.set(lng, lat)
      })

      setSunPosition()
      sunInterval = window.setInterval(setSunPosition, 60_000)
    })

    const resize = () => {
      if (!globe || !root) return
      globe.width(root.clientWidth)
      globe.height(root.clientHeight)
    }

    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(root)

    return () => {
      disposed = true
      window.clearInterval(sunInterval)
      observer.disconnect()
      dayNightMaterial?.dispose()
      globe?._destructor()
      globe = null
    }
  }, [maxCount, points])

  return (
    <div className="leaderboard-qrg" aria-label="Question response globe">
      <div ref={globeRootRef} className="leaderboard-qrg__globe" />
    </div>
  )
}

function CobeLeaderboardQuestionResponseGlobe({
  responseCount,
  responseEventsUrl,
  theme,
}: LeaderboardQuestionResponseGlobeProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const pingMarkersRef = useRef<Marker[]>([])

  useEffect(() => {
    const allEvents: LeaderboardQuestionResponseEventPoint[] = []
    const queuedEvents: LeaderboardQuestionResponseEventPoint[] = []
    let liveTargets: CobePingTarget[] = []
    let afterId = 0
    let afterResponseTime: string | null = null
    let exhausted = false
    let disposed = false
    let playbackStartedAt = performance.now()
    let shownThisLoop = 0

    const getResponseEventsUrl = () => {
      const url = responseEventsUrl
        ? new URL(responseEventsUrl, window.location.origin)
        : new URL(window.location.href)
      url.searchParams.set('handler', 'ResponseEvents')
      return url
    }

    const syncCobeTargets = () => {
      pingMarkersRef.current = cobeMarkersForTargets(liveTargets, performance.now())
    }

    const eventToTarget = (
      event: LeaderboardQuestionResponseEventPoint,
      index: number,
      now: number,
    ): CobePingTarget => ({
      lat: event.lat,
      lng: event.lng,
      maxRadius: 2.2 + (index % 5) * 0.35,
      createdAt: now,
      id: `target-${event.id}-${index}`,
    })

    const fetchNextPage = async () => {
      const url = getResponseEventsUrl()
      url.searchParams.set('afterId', afterId.toString())
      if (afterResponseTime) {
        url.searchParams.set('afterResponseTime', afterResponseTime)
      }
      url.searchParams.set('take', responseEventPageSize.toString())

      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Response event request failed: ${response.status}`)
      }

      const page = (await response.json()) as ResponseEventPage
      allEvents.push(...page.items)
      queuedEvents.push(...page.items)
      afterId = page.nextAfterId ?? afterId
      afterResponseTime = page.nextAfterResponseTime ?? afterResponseTime
      exhausted = !page.hasMore
    }

    const fetchLoop = async () => {
      try {
        while (!disposed && !exhausted) {
          if (queuedEvents.length < responseEventPageSize * 3) {
            await fetchNextPage()
          } else {
            await new Promise((resolve) => window.setTimeout(resolve, 500))
          }
        }
      } catch {
        exhausted = true
      }
    }

    syncCobeTargets()
    void fetchLoop()

    const playbackInterval = window.setInterval(() => {
      const now = performance.now()
      liveTargets = liveTargets.filter(
        (target) => now - target.createdAt <= targetLifetimeMs,
      )

      if (queuedEvents.length === 0 && exhausted && allEvents.length > 0) {
        queuedEvents.push(...allEvents)
        playbackStartedAt = now
        shownThisLoop = 0
      }

      const totalPlayable = Math.max(1, allEvents.length || responseCount)
      const targetShown = Math.min(
        totalPlayable,
        Math.floor(((now - playbackStartedAt) / playbackDurationMs) * totalPlayable),
      )
      const eventsToShow = Math.min(
        Math.max(0, targetShown - shownThisLoop),
        queuedEvents.length,
      )

      if (eventsToShow > 0) {
        const nextEvents = queuedEvents.splice(0, eventsToShow)
        liveTargets = [
          ...liveTargets,
          ...nextEvents.map((event, index) =>
            eventToTarget(event, shownThisLoop + index, now),
          ),
        ].slice(-maxLiveCobeTargets)
        shownThisLoop += nextEvents.length
      }

      syncCobeTargets()
    }, playbackTickMs)

    return () => {
      disposed = true
      window.clearInterval(playbackInterval)
      liveTargets = []
      pingMarkersRef.current = []
    }
  }, [responseCount, responseEventsUrl])

  useEffect(() => {
    const canvas = canvasRef.current
    const wrap = canvas?.parentElement
    if (!canvas || !wrap) return undefined

    let phi = initialCobePhi
    let theta = initialCobeTheta
    let width = 0
    let globe: CobeGlobe | null = null
    let animationFrame = 0
    let isDragging = false
    let lastPointerX = 0
    let lastPointerY = 0
    const isDark = theme === 'dark'

    const startGlobe = () => {
      width = Math.min(wrap.clientWidth, wrap.clientHeight || wrap.clientWidth)
      globe?.destroy()

      const options: COBEOptions = {
        devicePixelRatio: Math.min(2, window.devicePixelRatio || 1),
        width: width * 2,
        height: width * 2,
        phi,
        theta,
        dark: isDark ? 1 : 0,
        diffuse: isDark ? 1.15 : 1.2,
        mapSamples: 16000,
        mapBrightness: isDark ? 5.2 : 6,
        baseColor: [1, 1, 1],
        markerColor: [1, 0.36, 0.84],
        glowColor: [1, 1, 1],
        markers: pingMarkersRef.current,
        markerElevation: 0.035,
        scale: 1,
        offset: [0, 0],
      }

      globe = createGlobe(canvas, options)
    }

    startGlobe()

    const animate = () => {
      if (!isDragging) {
        phi += 0.002
      }
      globe?.update({
        phi,
        theta,
        markers: pingMarkersRef.current,
      })
      animationFrame = requestAnimationFrame(animate)
    }

    animate()

    const handlePointerDown = (event: PointerEvent) => {
      isDragging = true
      lastPointerX = event.clientX
      lastPointerY = event.clientY
      canvas.setPointerCapture(event.pointerId)
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (!isDragging) return

      const deltaX = event.clientX - lastPointerX
      const deltaY = event.clientY - lastPointerY
      lastPointerX = event.clientX
      lastPointerY = event.clientY

      phi += deltaX * 0.006
      theta = clamp(theta + deltaY * 0.006, -0.75, 0.75)
      globe?.update({ phi, theta })
    }

    const handlePointerUp = (event: PointerEvent) => {
      isDragging = false
      if (canvas.hasPointerCapture(event.pointerId)) {
        canvas.releasePointerCapture(event.pointerId)
      }
    }

    canvas.addEventListener('pointerdown', handlePointerDown)
    canvas.addEventListener('pointermove', handlePointerMove)
    canvas.addEventListener('pointerup', handlePointerUp)
    canvas.addEventListener('pointercancel', handlePointerUp)

    const observer = new ResizeObserver(() => {
      if (wrap.clientWidth === width) return
      startGlobe()
    })
    observer.observe(wrap)

    return () => {
      cancelAnimationFrame(animationFrame)
      canvas.removeEventListener('pointerdown', handlePointerDown)
      canvas.removeEventListener('pointermove', handlePointerMove)
      canvas.removeEventListener('pointerup', handlePointerUp)
      canvas.removeEventListener('pointercancel', handlePointerUp)
      observer.disconnect()
      globe?.destroy()
      globe = null
    }
  }, [theme])

  return (
    <div className="leaderboard-qrg" aria-label="Question response globe">
      <canvas
        ref={canvasRef}
        className="leaderboard-qrg__canvas"
        width="360"
        height="360"
      />
    </div>
  )
}

export default function LeaderboardQuestionResponseGlobe(
  props: LeaderboardQuestionResponseGlobeProps,
) {
  const renderer = useMemo(getSelectedRenderer, [])

  if (renderer === 'cobe') {
    return <CobeLeaderboardQuestionResponseGlobe {...props} />
  }

  return <GlobeGlLeaderboardQuestionResponseGlobe {...props} />
}
